import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Loader2, Package2 } from 'lucide-react';
import ReportArtistModal from '../components/ReportArtistModal';
import PressingMultiTrackPlayer from '../components/pressing/PressingMultiTrackPlayer';
import TipTapContentRenderer from '../components/profile/TipTapContentRenderer';
import { checkFanSession, getFanPressingDetail } from '../services/api';
import { sanitizeLinerNotesHtml } from '../utils/sanitizeLinerNotesHtml';

interface ViewVaultPageProps {
  vaultId: string;
  tenantId?: number;
  artistName?: string;
}

interface PressingTrack {
  order: number;
  trackId: number;
  title: string;
  label: string | null;
  streamUrl: string | null;
  durationSeconds: number | null;
  lyrics: unknown[] | null;
  hasLyrics: boolean;
}

interface PressingDetailResponse {
  data: {
    id: number;
    title: string;
    coverUrl: string | null;
    issueDate: string | null;
    productionType: string | null;
    productionLimit: number | null;
    issueNumber: number | null;
    linerNotes: string | null;
    artist: {
      id: number;
      slug: string;
      name: string;
    };
    fan?: {
      userReference?: string | null;
    };
    tracks: PressingTrack[];
  };
}

function formatDuration(totalSeconds: number | null): string {
  if (!totalSeconds || !Number.isFinite(totalSeconds)) return '--:--';
  const secs = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(secs / 60);
  const rem = String(secs % 60).padStart(2, '0');
  return `${mins}:${rem}`;
}

function formatReleaseDate(raw: string | null): string | null {
  if (!raw) return null;
  const numeric = Number(raw);
  const date = Number.isFinite(numeric)
    ? new Date((String(Math.trunc(numeric)).length <= 10 ? numeric * 1000 : numeric))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getEditionText(productionType: string | null, productionLimit: number | null): string | null {
  if (!productionType) return null;
  const normalized = productionType.trim().toLowerCase();

  if (normalized.includes('unlimited') || normalized.includes('timed')) {
    return null;
  }

  if (normalized.includes('limited')) {
    if (productionLimit && Number.isFinite(productionLimit) && productionLimit > 0) {
      return `Limited Edition · ${productionLimit} copies`;
    }
    return 'Limited Edition';
  }

  return null;
}

function isTipTapJsonContent(content: string): boolean {
  try {
    const parsed = JSON.parse(content);
    return !!parsed && typeof parsed === 'object' && (parsed as { type?: unknown }).type === 'doc';
  } catch {
    return false;
  }
}

export default function ViewVaultPage({ vaultId, tenantId, artistName }: ViewVaultPageProps) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detail, setDetail] = useState<PressingDetailResponse['data'] | null>(null);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);
  const [playRequestId, setPlayRequestId] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [trackListMaxHeight, setTrackListMaxHeight] = useState<number | null>(null);
  const [showTrackFade, setShowTrackFade] = useState(false);
  const artworkRef = useRef<HTMLDivElement | null>(null);
  const trackListHeaderRef = useRef<HTMLDivElement | null>(null);
  const trackListRef = useRef<HTMLUListElement | null>(null);
  const trackButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    let cancelled = false;

    async function loadPressing() {
      setStatus('loading');
      setErrorMessage(null);

      try {
        const sessionRes = await checkFanSession();
        if (!sessionRes.ok) {
          window.location.href = `/fan-login?redirect=${encodeURIComponent(window.location.pathname)}`;
          return;
        }

        const res = await getFanPressingDetail(vaultId);

        if (res.status === 401) {
          window.location.href = `/fan-login?redirect=${encodeURIComponent(window.location.pathname)}`;
          return;
        }

        if (res.status === 403) {
          throw new Error('You do not have access to this pressing.');
        }

        if (res.status === 404) {
          throw new Error('This pressing was not found.');
        }

        if (!res.ok) {
          throw new Error('Failed to load pressing details.');
        }

        const payload = await res.json() as PressingDetailResponse;
        if (cancelled) return;

        setDetail(payload.data);
        setSelectedTrackIndex(0);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : 'Something went wrong while loading this pressing.');
        setStatus('error');
      }
    }

    loadPressing();

    return () => {
      cancelled = true;
    };
  }, [vaultId]);

  const effectiveTenantId = detail?.artist.id ?? tenantId;
  const effectiveArtistName = detail?.artist.name ?? artistName ?? 'this artist';
  const artistHref = detail?.artist.slug ? `/artist/${detail.artist.slug}` : null;
  const linerNotes = detail?.linerNotes?.trim() || '';
  const releaseDate = formatReleaseDate(detail?.issueDate || null);
  const editionText = getEditionText(detail?.productionType || null, detail?.productionLimit || null);
  const copyNumber = detail?.issueNumber ?? null;
  const copyMilestoneText = copyNumber === 1
    ? 'First to collect this Pressing'
    : copyNumber && copyNumber <= 10
      ? 'One of the first to collect this Pressing'
      : null;
  const linerNotesIsTipTap = linerNotes ? isTipTapJsonContent(linerNotes) : false;
  const linerNotesHtml = useMemo(() => {
    if (!linerNotes || linerNotesIsTipTap) return '';
    return sanitizeLinerNotesHtml(linerNotes);
  }, [linerNotes, linerNotesIsTipTap]);

  useEffect(() => {
    const listEl = trackListRef.current;
    if (!listEl) return;

    const updateFade = () => {
      const hasOverflow = listEl.scrollHeight > listEl.clientHeight + 1;
      const atEnd = listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 2;
      setShowTrackFade(hasOverflow && !atEnd);
    };

    updateFade();
    listEl.addEventListener('scroll', updateFade);
    window.addEventListener('resize', updateFade);

    return () => {
      listEl.removeEventListener('scroll', updateFade);
      window.removeEventListener('resize', updateFade);
    };
  }, [detail?.id, trackListMaxHeight]);

  useEffect(() => {
    if (!detail) return;

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const MIN_DESKTOP_LIST_HEIGHT = 120;
    const MAX_DESKTOP_LIST_HEIGHT = 350;
    const MOBILE_LIST_HEIGHT = 300;

    const recalc = () => {
      if (!artworkRef.current || !trackListHeaderRef.current) return;

      if (!mediaQuery.matches) {
        setTrackListMaxHeight(MOBILE_LIST_HEIGHT);
        return;
      }

      const artworkHeight = artworkRef.current.getBoundingClientRect().height;
      const headerHeight = trackListHeaderRef.current.getBoundingClientRect().height;
      const available = Math.floor(artworkHeight - headerHeight);

      if (!Number.isFinite(available)) return;
      const clampedDesktopHeight = Math.min(
        MAX_DESKTOP_LIST_HEIGHT,
        Math.max(MIN_DESKTOP_LIST_HEIGHT, available),
      );
      setTrackListMaxHeight(clampedDesktopHeight);
    };

    recalc();

    const resizeObserver = new ResizeObserver(recalc);
    if (artworkRef.current) resizeObserver.observe(artworkRef.current);
    if (trackListHeaderRef.current) resizeObserver.observe(trackListHeaderRef.current);

    const onViewportChange = () => recalc();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onViewportChange);
    } else {
      mediaQuery.addListener(onViewportChange);
    }
    window.addEventListener('resize', recalc);

    return () => {
      resizeObserver.disconnect();
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', onViewportChange);
      } else {
        mediaQuery.removeListener(onViewportChange);
      }
      window.removeEventListener('resize', recalc);
    };
  }, [detail?.id]);

  useEffect(() => {
    const listEl = trackListRef.current;
    const selectedEl = trackButtonRefs.current[selectedTrackIndex];
    if (!listEl || !selectedEl) return;

    const listRect = listEl.getBoundingClientRect();
    const selectedRect = selectedEl.getBoundingClientRect();
    const outsideTop = selectedRect.top < listRect.top;
    const outsideBottom = selectedRect.bottom > listRect.bottom;

    if (outsideTop || outsideBottom) {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedTrackIndex]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
          <p className="text-xs text-text-muted font-mono">loading_pressing...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || !detail) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex flex-col">
        <header className="sticky top-0 z-40 glass-card border-b border-white/5 rounded-none px-4 sm:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md text-center space-y-3">
            <h1 className="text-xl font-semibold text-white">Unable to Open Pressing</h1>
            <p className="text-text-secondary text-sm">{errorMessage || 'Please try again in a moment.'}</p>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="mt-2 inline-flex items-center px-4 py-2 rounded-lg border border-white/20 text-sm text-white hover:border-electric-cyan"
            >
              Return to My Collection
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal flex flex-col">
      <header className="sticky top-0 z-40 glass-card border-b border-white/5 rounded-none px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-1.5 text-sm text-text-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">{detail.title}</h1>
            <p className="text-xs font-mono text-text-muted">PRESSING-{detail.id}</p>
          </div>
          {artistHref ? (
            <a
              href={artistHref}
              className="inline-flex items-center gap-1.5 text-xs text-electric-cyan hover:text-electric-cyan/80"
            >
              Visit Artist <ExternalLink className="w-3 h-3" />
            </a>
          ) : null}
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <section className="rounded-2xl border border-white/10 bg-[#121212] p-4 sm:p-5 lg:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
              <div ref={artworkRef} className="aspect-square w-full bg-black/40 rounded-xl overflow-hidden">
                {detail.coverUrl ? (
                  <img
                    src={detail.coverUrl}
                    alt={`${detail.title} cover`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <Package2 className="w-14 h-14" />
                  </div>
                )}
              </div>

              <div className="pt-1">
                <h2 className="text-2xl sm:text-3xl font-semibold text-white leading-tight">{detail.title}</h2>
                <p className="text-base text-text-secondary mt-2">{detail.artist.name}</p>

                <div className="mt-5 space-y-1.5 text-sm text-text-muted">
                  {releaseDate ? <p>Released {releaseDate}</p> : null}
                  {editionText ? <p>{editionText}</p> : null}
                  {copyNumber ? <p>Your copy #{copyNumber}</p> : null}
                  {copyMilestoneText ? <p>{copyMilestoneText}</p> : null}
                </div>

                <div className="mt-6 min-h-0 flex flex-col">
                  <div ref={trackListHeaderRef}>
                  <h3 className="text-white/90 text-sm font-semibold mb-3">Track Listing</h3>
                  </div>
                  {detail.tracks.length === 0 ? (
                    <p className="text-sm text-white/60">No tracks were found for this pressing.</p>
                  ) : (
                    <div className="relative">
                    <ul
                      ref={trackListRef}
                      className="divide-y divide-white/10 border-y border-white/10 overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:rgba(0,245,255,0.35)_transparent]"
                      style={trackListMaxHeight ? { maxHeight: `${trackListMaxHeight}px` } : undefined}
                    >
                      {detail.tracks.map((track, index) => {
                        const active = index === selectedTrackIndex;
                        return (
                          <li key={track.trackId}>
                            <button
                              ref={(node) => {
                                trackButtonRefs.current[index] = node;
                              }}
                              type="button"
                              onClick={() => {
                                setSelectedTrackIndex(index);
                                setPlayRequestId((prev) => prev + 1);
                              }}
                              className="w-full text-left py-3 px-1"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex items-center gap-3">
                                  <span className={`text-xs font-mono ${active ? 'text-electric-cyan' : 'text-white/45'}`}>
                                    {String(track.order).padStart(2, '0')}
                                  </span>
                                  <span className={`text-sm truncate ${active ? 'text-electric-cyan' : 'text-white/90'}`}>
                                    {track.title}
                                  </span>
                                </div>
                                {track.durationSeconds && Number.isFinite(track.durationSeconds) ? (
                                  <span className={`text-xs font-mono ${active ? 'text-electric-cyan/90' : 'text-white/55'}`}>
                                    {formatDuration(track.durationSeconds)}
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {showTrackFade ? (
                      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#121212] to-transparent" />
                    ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section>
            <PressingMultiTrackPlayer
              tracks={detail.tracks}
              selectedIndex={selectedTrackIndex}
              onSelectedIndexChange={setSelectedTrackIndex}
              playRequestId={playRequestId}
            />
          </section>

          {linerNotes ? (
            <section className="w-full pb-4">
              <h3 className="text-white font-semibold mb-4">Liner Notes</h3>
              {linerNotesIsTipTap ? (
                <TipTapContentRenderer content={linerNotes} />
              ) : (
                <div
                  className="prose prose-invert max-w-none prose-p:text-gray-300 prose-headings:text-white prose-a:text-electric-cyan prose-li:text-gray-300"
                  dangerouslySetInnerHTML={{ __html: linerNotesHtml }}
                />
              )}
            </section>
          ) : null}
        </div>
      </main>

      {effectiveTenantId ? (
        <footer className="px-4 sm:px-6 pb-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => setShowReport(true)}
              className="text-[11px] text-text-muted/50 hover:text-text-muted transition-colors font-mono"
            >
              Report this artist
            </button>
          </div>
        </footer>
      ) : null}

      {showReport && effectiveTenantId && (
        <ReportArtistModal
          tenantId={effectiveTenantId}
          artistName={effectiveArtistName}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}
